import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import LoadingPopup from '../misc/Loading.js';

const FrameworkReview = () => {
  const navigate = useNavigate();
  const { role } = useAuth();
  const isViewer = role === 'viewer';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryStatus, setCategoryStatus] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [responses, setResponses] = useState({});
  const [followUpResponses, setFollowUpResponses] = useState({});

  const selectedApp = sessionStorage.getItem('selectedApp');

  // Fetch the full startup bundle together so the UI does not render partial state.
  useEffect(() => {
    let isMounted = true;

    const loadFrameworkReview = async () => {
      setLoading(true);

      const questionsRequest = fetch('/questions.json').then((res) => {
        if (!res.ok) {
          throw new Error(`questions.json request failed with status ${res.status}`);
        }
        return res.json();
      });
      const categoryStatusRequest = API.post('category-status/', { assetName: selectedApp });
      const responsesRequest = API.get('responses/', {
        params: { assetName: selectedApp }
      });

      const [questionsResult, categoryStatusResult, responsesResult] = await Promise.allSettled([
        questionsRequest,
        categoryStatusRequest,
        responsesRequest
      ]);

      if (!isMounted) return;

      if (questionsResult.status === 'fulfilled') {
        const jsonData = questionsResult.value;
        const dataWithIds = jsonData.map((q, index) => ({
          ...q,
          id: `question-${index}`
        }));
        setData(dataWithIds);
        setCategories([...new Set(jsonData.map((q) => q.category))]);
      } else {
        console.error('Error loading questions.json:', questionsResult.reason);
      }

      if (categoryStatusResult.status === 'fulfilled') {
        setCategoryStatus(categoryStatusResult.value.data);
      } else {
        console.error('Error fetching category status:', categoryStatusResult.reason);
      }

      if (responsesResult.status === 'fulfilled') {
        const savedResponses = responsesResult.value.data;

        const mainResponses = {};
        const followUps = {};

        savedResponses.forEach((r) => {
          if (r.follow_up_index === null) {
            mainResponses[r.question_id] = r.response;
          } else {
            if (!followUps[r.question_id]) followUps[r.question_id] = {};
            followUps[r.question_id][r.follow_up_index] = r.response;
          }
        });

        setResponses(mainResponses);
        setFollowUpResponses(followUps);
      } else {
        console.error('Error fetching saved responses:', responsesResult.reason);
      }

      setLoading(false);
    };

    loadFrameworkReview();

    return () => {
      isMounted = false;
    };
  }, [selectedApp]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const autosaveResponse = async (questionId, response, followUpIndex = null) => {
    if (isViewer) return;

    try {
      const payload = { assetName: selectedApp, questionId, response, followUpIndex, category: selectedCategory };

      await API.post('save-response/', payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      // Update category status locally
      const statusResponse = await API.post('category-status/', { assetName: selectedApp });
      setCategoryStatus(statusResponse.data);
    } catch (error) {
      console.error('Error autosaving response:', error);
    }
  };

  const handleResponseSelect = (questionId, response) => {
    if (isViewer) return;

    setResponses((prev) => {
      const updated = { ...prev, [questionId]: response };
      autosaveResponse(questionId, response);
      return updated;
    });

    setFollowUpResponses((prev) => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
  };

  const handleFollowUpResponseSelect = (questionId, followUpIndex, response) => {
    if (isViewer) return;

    setFollowUpResponses((prev) => {
      const existingFollowUps = prev[questionId] || {};
      const updated = { ...prev, [questionId]: { ...existingFollowUps, [followUpIndex]: response } };
      autosaveResponse(questionId, response, followUpIndex);
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (isViewer) return;

    try {
      // Calculate scores by calling the recalculate endpoint
      await API.post('recalculate-scores/', { assetName: selectedApp });

      // Navigate to dashboard after calculation
      navigate('/dashboard');
    } catch (error) {
      console.error('Error calculating scores:', error);
      // Navigate anyway
      navigate('/dashboard');
    }
  };

  const filteredQuestions = data.filter((q) => q.category === selectedCategory);

  return (
    <div className="p-4 space-y-6 text-[0.95rem] sm:text-[1rem]">
      {loading && <LoadingPopup isVisible={true} />}

      <div className="grid grid-cols-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {categories.map((cat, idx) => {
          const categoryData = categoryStatus[cat] || { status: 'Incomplete', score: 0 };
          const isComplete = categoryData.status === 'Complete';
          const isSelected = selectedCategory === cat;

          return (
            <div
              key={idx}
              onClick={() => handleCategoryClick(cat)}
              className={`p-3 rounded-lg cursor-pointer text-center transition-all 
                ${isSelected ? 'bg-blue-500/10' : 'bg-black/20'}
                border 
                ${isSelected
                  ? 'border-blue-500 text-blue-400'
                  : isComplete
                    ? 'border-green-400 text-green-400'
                    : 'border-red-400 text-red-400'}
              `}
            >
              <h3 className="text-sm sm:text-base font-semibold">{cat}</h3>
              <p className="text-xs sm:text-sm mt-1 font-medium">
                {categoryData.status}
              </p>
            </div>
          );
        })}
      </div>

      {isViewer && (
        <div className="rounded-lg border border-blue-500/40 bg-blue-500/10 px-4 py-3 text-sm text-blue-300">
          Viewer access: previous framework review settings are read-only.
        </div>
      )}

      {/* Submit Button */}
      {!isViewer && (
        <div className="flex justify-center">
          <button
            onClick={handleSubmit}
            className="px-6 py-3 hover:bg-blue-700/50 hover:text-white text-blue-500 font-semibold rounded-lg transition-all border border-blue-500"
          >
            Submit
          </button>
        </div>
      )}

      {/* Questions List */}
      {selectedCategory && (
        <div className="space-y-4 mt-4">
          <h3 className="text-md sm:text-lg font-semibold text-gray-200">
            Questions - {selectedCategory}
          </h3>
          {filteredQuestions.map((question) => {
            const selectedResponse = responses[question.id];
            const followUps =
              selectedResponse && question.follow_ups
                ? question.follow_ups[selectedResponse] || []
                : [];

            return (
              <div
                key={question.id}
                className="p-4 rounded-lg border border-gray-700 bg-black/20 space-y-3"
              >
                <p className="text-gray-200 text-sm sm:text-base">{question.main_question}</p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <span
                    className={`px-2 py-0.5 rounded text-xs sm:text-sm font-medium ${
                      question.criticality === 'High'
                        ? 'bg-red-500/20 text-red-400'
                        : question.criticality === 'Medium'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-blue-500/20 text-blue-400'
                    }`}
                  >
                    {question.criticality}
                  </span>
                  <span className="px-2 py-0.5 rounded text-xs sm:text-sm font-medium bg-gray-700 text-gray-300">
                    Score: {question.score}
                  </span>
                </div>

                {/* Response Buttons */}
                <div className="flex gap-3 mt-3">
                  {['Yes', 'No', 'Not Sure'].map((resp) => (
                    <button
                      key={resp}
                      onClick={() => handleResponseSelect(question.id, resp)}
                      disabled={isViewer}
                      className={`px-4 py-2 rounded-lg text-sm sm:text-base font-medium border transition-all ${
                        selectedResponse === resp
                          ? 'bg-blue-500/10 border-blue-500 text-blue-400'
                          : 'bg-black/20 border-gray-700 text-gray-200'
                      } ${
                        isViewer
                          ? 'cursor-not-allowed opacity-70'
                          : 'hover:border-gray-600'
                      }`}
                    >
                      {resp}
                    </button>
                  ))}
                </div>

                {/* Follow-up Questions */}
                {followUps.length > 0 && (
                  <div className="space-y-2 mt-4">
                    <h4 className="text-sm sm:text-base font-semibold text-gray-300">
                      Follow-up Questions
                    </h4>
                    {followUps.map((followUp, fIdx) => (
                      <div
                        key={`${question.id}-${fIdx}`}
                        className="p-3 rounded-lg bg-black/30 border border-gray-700 space-y-2"
                      >
                        <p className="text-sm sm:text-base text-gray-300">{followUp.question}</p>
                        <div className="flex gap-3 mt-2">
                          {['Yes', 'No', 'Not Sure'].map((resp) => (
                            <button
                              key={resp}
                              onClick={() =>
                                handleFollowUpResponseSelect(question.id, fIdx, resp)
                              }
                              disabled={isViewer}
                              className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium border transition-all ${
                                followUpResponses[question.id]?.[fIdx] === resp
                                  ? 'bg-blue-500/10 border-blue-500 text-blue-400'
                                  : 'bg-black/20 border-gray-700 text-gray-200'
                              } ${
                                isViewer
                                  ? 'cursor-not-allowed opacity-70'
                                  : 'hover:border-gray-600'
                              }`}
                            >
                              {resp}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FrameworkReview;
